import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  DocumentIcon,
  DocumentTextIcon,
  PhotoIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { getPortalDocuments, downloadPortalDocument, uploadPortalDocument } from '../../services/portalService';
import Spinner from '../../components/common/Spinner';
import EmptyState from '../../components/common/EmptyState';
import SkeletonBlock from '../../components/common/SkeletonBlock';

const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function getFileIcon(fileName) {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) return PhotoIcon;
  if (['pdf', 'docx', 'txt'].includes(ext)) return DocumentTextIcon;
  return DocumentIcon;
}

export default function PortalDocuments() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const { data: documents, isLoading } = useQuery({
    queryKey: ['portal', 'documents'],
    queryFn: getPortalDocuments,
  });

  const handleDownload = async (doc) => {
    try {
      await downloadPortalDocument(doc.id, doc.fileName);
    } catch {
      toast.error('Failed to download document');
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    try {
      await uploadPortalDocument(file);
      await queryClient.invalidateQueries({ queryKey: ['portal', 'documents'] });
      toast.success('Document uploaded');
    } catch {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1>Documents</h1>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="btn btn-primary disabled:opacity-60"
        >
          {uploading ? <Spinner size="sm" /> : <ArrowUpTrayIcon className="h-4 w-4" />}
          Upload
        </button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.docx,.xlsx,.txt"
          onChange={handleFileChange}
        />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card space-y-3">
              <SkeletonBlock className="h-10 w-10" rounded="rounded-xl" />
              <SkeletonBlock className="h-4 w-3/4" rounded="rounded" />
              <SkeletonBlock className="h-3 w-1/2" rounded="rounded" />
            </div>
          ))}
        </div>
      ) : !documents?.length ? (
        <EmptyState
          icon={DocumentIcon}
          title="No documents"
          description="Upload documents or view documents shared by your practice here."
          action={
            <button onClick={() => fileInputRef.current?.click()} className="btn btn-primary">
              Upload document
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {documents.map((doc) => {
            const FileIcon = getFileIcon(doc.fileName);
            return (
              <div key={doc.id} className="card border-t-2 border-t-teal-200 dark:border-t-teal-800 flex flex-col gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center shrink-0">
                    <FileIcon className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                      {doc.fileName}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                      {formatBytes(doc.fileSize)}
                      {doc.uploader && ` · ${doc.uploader.firstName} ${doc.uploader.lastName}`}
                    </p>
                    <p className="text-xs text-neutral-400 dark:text-neutral-500">
                      {format(new Date(doc.createdAt), 'MMM d, yyyy')}
                    </p>
                  </div>
                </div>

                {doc.description && (
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                    {doc.description}
                  </p>
                )}

                <button
                  onClick={() => handleDownload(doc)}
                  className="btn btn-secondary btn-sm w-full justify-center"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Download
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
